import { ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { Params } from '@angular/router';
import { IonInfiniteScroll, ModalController } from '@ionic/angular';
import { PaginationData } from '@ngneat/elf-pagination';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PaginatedResponse } from '@tcc-mono/shared/data-access';
import { StackedListField } from '@tcc-mono/shared/ui/stacked-list';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { Relation } from '@tcc-mono/vandijk/shared/data-access/models';
import { RelationService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, tap, switchMap, take, map } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'tcc-mono-select-customer-dialog',
  templateUrl: './select-customer-dialog.component.html',
  styleUrls: ['./select-customer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectCustomerDialogComponent implements OnInit {
  private readonly _service: RelationService = inject(RelationService);
  private readonly _modalCtrl: ModalController = inject(ModalController);

  @Input() public withEmptyOption: boolean = false;
  @Input() public params: Params = {};

  private _customers = new BehaviorSubject<Relation[]>([]);
  public customers$: Observable<Relation[]> = this._customers.asObservable()
    .pipe(
      map((relations: Relation[]) => {
        if (this.withEmptyOption) {
          relations.unshift({
            ...new Relation(),
            name: 'Geen'
          });
        }
        return relations;
      })
    );

  private _loading = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this._loading.asObservable();

  private _q = new BehaviorSubject<string>('');
  public q$: Observable<string> = this._q.asObservable().pipe(debounceTime(300), distinctUntilChanged())

  private _pagination: PaginationData;

  public fields: StackedListField[] = [
    {
      property: 'name'
    },
    {
      property: (customer: Relation) => `${customer.visit_address || ''}${customer.visit_address && customer.visit_city ? ', ' : ''}${customer.visit_city || ''}`,
      icon: 'house-window'
    }
  ];

  @ViewChild('infiniteScroll') public infiniteScroll: IonInfiniteScroll;

  public ngOnInit(): void {
    this.q$
      .pipe(
        tap((val: string) => {
          //this.infiniteScroll.disabled = false;
          this.params = {
            ...this.params,
            page: 1,
            q: val
          };
        }),
        switchMap(() => this._getCustomers(true)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public setQ(val: string) {
    this._q.next(val);
  }

  public loadData(event: any) {
    if (this._pagination.currentPage < this._pagination.lastPage) {
      this.params = {
        ...this.params,
        page: this._pagination.currentPage + 1
      };

      this._getCustomers(false)
        .pipe(
          take(1)
        )
        .subscribe({
          next: () => event.target.complete()
        });

    } else {
      event.target.complete();
    }
  }

  public selectCustomer(customer: Relation) {
    this._modalCtrl.dismiss(customer);
  }

  private _getCustomers = (replace: boolean = true): Observable<PaginatedResponse<Relation>> => {
    this._loading.next(true);

    return this._service.getAll(this.params)
      .pipe(
        tap(({ data, meta }) => {
          this._pagination = ElfUtil.convertMetaToElfPagination(meta);
          this._customers.next(replace ? data : [...this._customers.value, ...data]);
          this._loading.next(false);
        })
      );
  }
}
